import { useEffect, useState, useCallback } from "react";
import { Sparkles, Bot, TrendingUp, TrendingDown, Pause, Send } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { http, errMsg } from "@/lib/api";
import SubscribersPanel from "@/components/SubscribersPanel";
import GoldChart from "@/components/gold/GoldChart";
import GoldHistory from "@/components/gold/GoldHistory";

const STYLE = {
  BUY: { color: "var(--win)", icon: TrendingUp, label: "COMPRA" },
  SELL: { color: "var(--loss)", icon: TrendingDown, label: "VENDI" },
  ATTENDI: { color: "var(--draw)", icon: Pause, label: "ATTENDI" },
};
const n = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

function SignalCard({ sig }) {
  if (!sig) return <div data-testid="gold-no-signal" className="panel p-6 text-slate-400">Nessun segnale ancora. Premi "Analizza ora".</div>;
  const d = sig.data;
  const st = STYLE[d.segnale];
  const Icon = st.icon;
  return (
    <div data-testid="gold-signal-card" className="panel p-6 sm:p-8 space-y-5" style={{ borderColor: st.color }}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <span className="font-mono text-xs text-slate-400 tracking-widest">ULTIMO SEGNALE · {new Date(sig.created_at).toLocaleString("it-IT")}</span>
          <div data-testid="gold-signal-value" className="font-display text-6xl font-extrabold flex items-center gap-3" style={{ color: st.color }}><Icon size={44} /> {st.label}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-400 uppercase">Prezzo</div>
          <div className="font-display text-3xl font-bold">{n(sig.price)}</div>
          <div className="text-xs font-mono text-slate-400">Fiducia {d.fiducia}%</div>
        </div>
      </div>
      {d.segnale !== "ATTENDI" && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[["Entrata", d.entrata], ["Stop loss", d.stop_loss], ["TP1", d.take_profit_1], ["TP2", d.take_profit_2]].map(([l, v]) => (
            <div key={l} className="border border-slate-800 rounded-lg p-3"><div className="text-[11px] uppercase text-slate-400">{l}</div><div className="font-mono font-semibold">{n(v)}</div></div>
          ))}
        </div>
      )}
      <div data-testid="gold-action" className="rounded-lg border border-slate-700 bg-slate-900/60 p-4 text-sm space-y-1">
        <div className="text-[11px] uppercase tracking-wider text-slate-400">Cosa fare</div>
        {d.segnale === "ATTENDI" ? (
          <>
            <div className="font-semibold">Non entrare ora</div>
            {d.compra_sopra && <div className="text-[var(--win)]">Compra se supera {n(d.compra_sopra)}</div>}
            {d.vendi_sotto && <div className="text-[var(--loss)]">Vendi se scende sotto {n(d.vendi_sotto)}</div>}
          </>
        ) : <div className="font-semibold" style={{ color: st.color }}>{d.segnale === "BUY" ? "Compra" : "Vendi"} ora intorno a {n(d.entrata)}</div>}
        {d.azione && <div className="text-slate-300">👉 {d.azione}</div>}
      </div>
      <p data-testid="gold-signal-analysis" className="text-slate-300 leading-relaxed">{d.analisi}</p>
      <ul className="text-sm text-slate-400 space-y-1">{d.fattori?.map((f, i) => <li key={i}>• {f}</li>)}</ul>
    </div>
  );
}

const ASSETS = { XAU: { tag: "ORO", title: "XAU / USDT" }, BTC: { tag: "BITCOIN", title: "BTC / USDT" } };

export default function GoldPanel() {
  const [asset, setAsset] = useState("XAU");
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const [reload, setReload] = useState(0);
  const load = useCallback(() => http.get("/gold/status", { params: { asset } }).then((r) => setSt(r.data)), [asset]);
  useEffect(() => { load(); }, [load, reload]);

  const analyze = async (broadcast) => {
    setBusy(broadcast ? "send" : "an");
    try {
      await http.post("/gold/analyze", null, { params: { broadcast, asset } });
      toast.success(broadcast ? "Segnale generato e inviato agli iscritti" : "Nuovo segnale generato");
      setReload((x) => x + 1);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const save = async (patch) => {
    const r = await http.put("/gold/settings", patch);
    setSt((x) => ({ ...x, settings: r.data }));
    toast.success("Impostazioni salvate");
  };
  if (!st || st.asset !== asset) return <div className="panel h-64 scan" />;

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div data-testid="asset-switch" className="inline-flex mb-3 rounded-full border border-slate-800 p-1">
            {Object.entries(ASSETS).map(([k, a]) => (
              <button key={k} data-testid={`asset-${k.toLowerCase()}-btn`} onClick={() => setAsset(k)} className={`px-4 py-1 rounded-full text-xs font-mono tracking-widest transition-colors ${asset === k ? "bg-[var(--gold)] text-slate-950" : "text-slate-400 hover:text-white"}`}>{a.tag}</button>
            ))}
          </div>
          <span className="block font-mono text-xs text-[var(--gold)] tracking-widest">SEGNALI AI · {ASSETS[asset].tag}</span>
          <h2 className="font-display text-4xl font-extrabold uppercase flex items-center gap-3">{ASSETS[asset].title}
            <span data-testid="gold-market-status" className={`text-xs font-mono px-2 py-1 rounded ${st.market_open ? "bg-[var(--win)]/15 text-[var(--win)]" : "bg-[var(--loss)]/15 text-[var(--loss)]"}`}>{st.market_open ? "● MERCATO APERTO" : "🔒 MERCATO CHIUSO"}</span>
          </h2>
          <a data-testid="gold-bot-link" href={`https://t.me/${st.username}`} target="_blank" rel="noreferrer" className="text-sm text-[var(--tg)] flex items-center gap-1"><Bot size={14} /> @{st.username} · {st.running ? "attivo" : "attivo solo sul sito pubblicato"}</a>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="gold-analyze-btn" disabled={!!busy} onClick={() => analyze(false)} className={`btn btn-ghost ${busy === "an" ? "scan" : ""}`}><Sparkles size={16} /> {busy === "an" ? "Analisi..." : "Analizza ora"}</button>
          <button data-testid="gold-analyze-send-btn" disabled={!!busy} onClick={() => analyze(true)} className={`btn btn-gold ${busy === "send" ? "scan" : ""}`}><Send size={16} /> {busy === "send" ? "Invio..." : "Analizza e invia"}</button>
        </div>
      </div>
      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
        <SignalCard sig={st.last_signal} />
        <GoldChart signal={st.last_signal} asset={asset} />
      </div>
      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6 items-start">
        <GoldHistory reload={reload} asset={asset} />
        <div className="space-y-6">
          <div data-testid="gold-settings" className="panel p-6 space-y-4">
            <h3 className="font-display text-2xl font-bold uppercase">Invio automatico</h3>
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-slate-300">Segnale AI automatico + avvisi TP/SL</span>
              <Switch data-testid="gold-enabled-switch" checked={st.settings.enabled} onCheckedChange={(v) => save({ enabled: v })} />
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-slate-300">Segnali Bitcoin (BTC/USDT, 24/7)</span>
              <Switch data-testid="btc-enabled-switch" checked={st.settings.btc_enabled !== false} onCheckedChange={(v) => save({ btc_enabled: v })} />
            </div>
            <div className="flex items-center gap-3 text-sm">
              Ogni
              <select data-testid="gold-interval-select" value={st.settings.interval_hours} onChange={(e) => save({ interval_hours: Number(e.target.value) })} className="bg-slate-950 border border-slate-800 rounded-md px-2 py-1 font-mono">
                {[1, 2, 4, 6, 12, 24].map((h) => <option key={h} value={h}>{`${h} ${h === 1 ? "ora" : "ore"}`}</option>)}
              </select>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-slate-300">Invia solo BUY/SELL (salta gli ATTENDI ripetuti)</span>
              <Switch data-testid="gold-only-actionable-switch" checked={!!st.settings.only_actionable} onCheckedChange={(v) => save({ only_actionable: v })} />
            </div>
            <p className="text-xs text-slate-500">Oro: mercato chiuso da venerdì 22:00 a lunedì 00:00 (un solo avviso di chiusura). Bitcoin: segnali 24/7.</p>
            <p className="text-xs text-slate-500">Ogni 5 minuti il bot controlla anche se il prezzo tocca TP1, TP2 o stop loss e avvisa subito gli iscritti.</p>
          </div>
          <SubscribersPanel base="/gold/subscribers" title={`Utenti bot oro (${st.subscribers})`} />
        </div>
      </div>
    </div>
  );
}
